import CryptoJS from 'crypto-js';
import qs from 'qs';
import { Message } from '@arco-design/web-react';
import dayjs from 'dayjs';
import { DefaultParams } from './constant';
import {
  ERROR_ICON,
  Fields,
  FilterField,
  SUCCESS_ICON,
  SceneTypeMap,
} from './enum';
import { Filter, State } from './interface';
import { SearchType } from '@/constants/media';

interface Reference {
  field: FilterField;
  newValue?: any;
  oldValue?: any;
  displayName?: string;
}
export function getDateTimeByString(date: string) {
  if (DateCache.has(date)) {
    return DateCache.get(date);
  }
  const format = dayjs(date).format('YYYY.M.D HH:mm');
  DateCache.set(date, format);
  return format;
}

export class FilterStack extends Map<string, Filter> {
  emplace(reference: Reference, fn?: (value: any) => string, thisArg?: any) {
    const { field, newValue, oldValue, displayName } = reference;
    if (oldValue == null) {
      return this.set(reference.field, {
        field,
        value: newValue,
        displayName: displayName ?? fn?.call(thisArg, newValue) ?? '',
      });
    }
    const oldSet = new Set(oldValue);
    const newSet = new Set(newValue);
    const symmetricDifference = oldSet.symmetricDifference(newSet);
    if (!symmetricDifference.size) {
      return this;
    }
    const [modifyValue] = symmetricDifference;
    const key = `${field} ${modifyValue}`;
    // stack中无记录，真值存在变更，即通过popstate初始化的数据并不会事先存在操作记录
    if (this.has(key)) {
      this.delete(key);
      return this;
    }
    if (newSet.has(modifyValue)) {
      return this.set(key, {
        field,
        value: modifyValue,
        displayName: displayName ?? fn?.call(thisArg, modifyValue) ?? '',
      });
    }
    return this;
  }

  close(key: any, get: (field: string) => any) {
    const intermediate: Record<any, any> = {};
    const params: Record<any, any> = {};
    const filter = this.get(key);
    const { field } = filter;
    this.delete(key);
    if (Fields.has(key)) {
      // 单选
      params[field] = DefaultParams[field];
    } else {
      // 多选
      const set = new Set(get(field));
      set.delete(filter.value);
      // intermediate[field] = [...set];
      // TODO: 展开语法在打包会效果不一致，折磨，使用Array.from曲线救国
      intermediate[field] = Array.from(set);
      params[field] = String(intermediate[field]);
    }
    return { field, intermediate, params };
  }
}
Reflect.defineProperty(FilterStack.prototype, Symbol.toStringTag, {
  configurable: true,
  value: FilterStack.name,
});

function createOption<T>([value, label]: [T, string]) {
  return { value, label };
}

export function createOptions<T>(map: Map<T, string>) {
  return Array.from(map, createOption);
}

export function getField<T extends { field: string }>(entry: T) {
  return entry.field;
}

export function predicateFilter(
  this: Set<FilterField>,
  [, filter]: [string, Filter],
) {
  return this.has(filter.field);
}

const DateCache = new Map<string, string>();

export function getDateTime(data: any) {
  const date = data.datetime ?? data.uploaded_at;
  if (DateCache.has(date)) {
    return DateCache.get(date);
  }

  const format = dayjs(date).format('YYYY.M.D HH:mm');
  DateCache.set(date, format);
  return format;
}

export function writeText(data: string) {
  return navigator.clipboard.writeText(data);
}

export function loadHandler($event: React.SyntheticEvent<HTMLImageElement>) {
  $event.currentTarget.removeAttribute('loading');
}

export function $success(content: React.ReactNode | string) {
  Message.success({ className: 'search-message', content, icon: SUCCESS_ICON });
}

export function $error(content: React.ReactNode | string) {
  Message.error({ className: 'search-message', content, icon: ERROR_ICON });
}

export async function handlerIdClick(
  $event: React.MouseEvent<HTMLDivElement, MouseEvent>,
) {
  $event.preventDefault();
  await writeText(($event.currentTarget.lastChild as CharacterData).data);
  $success('ID已复制');
}

/**
 * 获取a标签外链
 * @param data 图片数据
 * @param type 查询类型
 * @param options 查询参数
 * @returns 超链接
 * @description TODO:
 * * search_position_term
 * * search_position_term
 * * requestId
 * * searchId
 * * searchFrom
 */
export function getHrefLink(data: any, state: State) {
  switch (state.params.type) {
    case SearchType.IMAGE:
      return `/v2/image?${qs.stringify({
        imageId: data.imageId,
        term: state.params.term,
        storyId: data.storyId,
        requestId: state.qid,
        searchId: state.searchId,
      })}`;
    case SearchType.STORY:
      if (data?.live) {
        return `/live?${qs.stringify({
          storyId: data.storyId,
          term: state.params.term,
          requestId: state.qid,
          searchId: state.searchId,
        })}`;
      } else {
        return `/story?${qs.stringify({
          storyId: data.storyId,
          term: state.params.term,
          requestId: state.qid,
          searchId: state.searchId,
        })}`;
      }

    case SearchType.CREATIVE:
      return `/v2/image?${qs.stringify({
        imageId: data.imageId,
        term: state.params.term,
        requestId: state.qid,
        searchId: state.searchId,
      })}`;
    case SearchType.VIDEO: {
      // 当为澎湃时跳转老页面路径从定向到澎湃站
      const defaultPath = data?.paper ? '/video' : '/v2/video';
      return `${defaultPath}?${qs.stringify({
        videoId: data.id,
        term: state.params.term,
        storyId: data.storyId,
        requestId: state.qid,
        searchId: state.searchId,
      })}`;
    }
    case SearchType.VIDEO_STORY:
      return `/video/story?${qs.stringify({
        storyId: data.storyId,
        requestId: state.qid,
        searchId: state.searchId,
      })}`;
    case SearchType.TOPIC:
      // 卡塔尔专题特殊路径
      if (Number(data.id) === 39570) {
        return `/v2/qatar2022?${qs.stringify({
          topicId: data.id,
          requestId: state.searchId,
          searchId: state.searchId,
        })}`;
      } else if (Number(data.id) === 39780) {
        return `/v2/aussieNz2023?${qs.stringify({
          topicId: data.id,
          requestId: state.searchId,
          searchId: state.searchId,
        })}`;
      } else {
        return `/topic?${qs.stringify({
          topicId: data.id,
          requestId: state.searchId,
          searchId: state.searchId,
        })}`;
      }
    default:
      return null;
  }
}

export function getId(data: any) {
  return data.id || getImageId(data);
}

export function getImageId(data: any) {
  return data.imageId;
}

export function getDesc(data: any) {
  return (
    data.title ||
    data.imageRemark ||
    data.imageRemarkEn ||
    data.remark ||
    data.remarkEn
  );
}

export function showAddGoodsSuccess(response: any) {
  if (response) {
    $success('加入购物车成功');
  } else {
    showAddGoodsError();
  }
  return response;
}

export function showAddGoodsError() {
  $error('加入购物车失败');
}

export function showCollectGoodsSuccess(response: any) {
  if (response.data.errno === 0) {
    $success('收藏成功');
  } else {
    showCollectGoodsError(response);
  }
  return response;
}

export function showCollectGoodsError(response: any) {
  $error(response.data?.errmsg || '收藏失败');
}

export function showDeleteCollectGoodsSuccess(response: any) {
  if (response.data.errno === 0) {
    $success('取消收藏');
  } else {
    showDeleteCollectGoodsError(response);
  }
  return response;
}

export function showDeleteCollectGoodsError(response: any) {
  $error(response.data?.errmsg || '操作失败');
}

/** 假设视频时长小于一小时，超过一小时则格式错误 */
const DURATION_FORMATTER = new Intl.DateTimeFormat(undefined, {
  minute: '2-digit',
  second: '2-digit',
});
export function convertDurationToTime(milliseconds: number) {
  return DURATION_FORMATTER.format(milliseconds);
}

export interface DataResponse extends Response {
  data: any;
}

export async function translateResponse(response: any): Promise<DataResponse> {
  try {
    response.data = await response.json();
  } catch {}
  return response;
}

export function getImageWith(screenWidth: number) {
  const mediaScreenMaxWidth = 284;
  const mediaScreenMinWidth = 240;
  const mediaScreenWidth = 245;
  const mediaScreenMinimumWidth = 220;

  // 屏幕宽度小于 1280
  if (screenWidth <= 1280) {
    return mediaScreenWidth;
  }
  // 屏幕宽度大于 1280 小于1366之间
  if (screenWidth > 1280 && screenWidth < 1366) {
    return mediaScreenMinWidth;
  }
  // 屏幕宽度大于等于 1366 小于1440之间
  if (screenWidth >= 1366 && screenWidth < 1440) {
    return mediaScreenMinimumWidth;
  }
  // 屏幕宽度大于等于 1440 小于1700之间
  if (screenWidth >= 1440 && screenWidth < 1700) {
    return mediaScreenMinWidth;
  }
  // 屏幕宽度大于等于 1700
  if (screenWidth >= 1700) {
    return mediaScreenMaxWidth;
  }
  return mediaScreenMaxWidth;
}

export const getSearchId = (
  scene: string,
  term: string,
  filter: string,
  sortBy: string,
) => {
  const userId =
    window.userId ||
    JSON.parse(
      window.localStorage.getItem('__tea_cache_tokens_20006662') || '{}',
    ).web_id ||
    '';
  // 获取当前时间戳，单位为秒，并取整除以 600
  const timestamp = Math.floor(Date.now() / 1000 / 600);

  const rawString = `${scene}${userId}${term}${filter}${sortBy}${timestamp}`;
  // 使用 crypto-js 生成 MD5 哈希值
  const hash = CryptoJS.MD5(rawString).toString(CryptoJS.enc.Hex);

  console.log(
    scene,
    userId,
    term,
    filter,
    sortBy,
    timestamp,
    hash,
    'getSearchId',
  );

  return hash;
};

export const handleSearchId = (obj, type) => {
  const _paramsObj = new URLSearchParams(obj);
  ['page', 'size', 'term', 'sortBy', 'searchId'].forEach(key => {
    if (_paramsObj.has(key)) {
      _paramsObj.delete(key);
    }
  });
  const scene = SceneTypeMap.get(type as SearchType);
  const _search_id = getSearchId(
    scene,
    obj.term,
    _paramsObj.toString(),
    obj.sortBy,
  );
  return _search_id;
};
